import { useState, useCallback } from 'react';
import { createPaymentPreference, redirectToCheckout } from '../services/mercadopagoService';
import { supabase } from '../lib/customSupabaseClient';

/**
 * Hook para gerenciar pagamentos com Mercado Pago
 */
export const usePayment = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Inicia o processo de pagamento
   * @param {Object} user - Dados do usuário logado
   */
  const initiatePayment = useCallback(async (user) => {
    if (!user) {
      setError('Usuário não encontrado');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Dados do usuário para o pagamento
      const userData = {
        userId: user.id,
        email: user.email,
        name: user.user_metadata?.full_name || user.email.split('@')[0]
      };

      console.log('Iniciando pagamento para usuário:', userData);

      // Criar preferência de pagamento
      const preferenceResult = await createPaymentPreference(userData);

      if (preferenceResult.success) {
        // Redirecionar para o checkout
        const checkoutUrl = preferenceResult.initPoint;
        redirectToCheckout(checkoutUrl);
      } else {
        setError(preferenceResult.error || 'Erro ao criar pagamento');
      }

    } catch (err) {
      console.error('Erro ao iniciar pagamento:', err);
      setError('Erro interno ao processar pagamento');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Atualiza status da assinatura após pagamento aprovado
   * @param {string} userId - ID do usuário
   * @param {string} paymentId - ID do pagamento
   */
  const updateSubscriptionAfterPayment = useCallback(async (userId, paymentId) => {
    try {
      const now = new Date();
      const subscriptionEndDate = new Date(now.getTime() + (365 * 24 * 60 * 60 * 1000)); // 1 ano

      const { error } = await supabase
        .from('user_subscriptions')
        .update({
          payment_status: 'active',
          subscription_start_date: now.toISOString(),
          subscription_end_date: subscriptionEndDate.toISOString(),
          last_payment_date: now.toISOString(),
          mercado_pago_payment_id: paymentId,
          updated_at: now.toISOString()
        })
        .eq('user_id', userId);

      if (error) {
        console.error('Erro ao atualizar assinatura:', error);
        throw error;
      }

      console.log('Assinatura atualizada com sucesso para o usuário:', userId);
      return { success: true };

    } catch (err) {
      console.error('Erro ao atualizar assinatura:', err);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Verifica se o usuário tem pagamento pendente
   * @param {string} userId - ID do usuário
   */
  const checkPendingPayment = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Erro ao verificar pagamento pendente:', error);
        return { hasPending: false };
      }

      // Verifica se há pagamento pendente baseado no status
      const hasPending = data.payment_status === 'pending' || 
                        (data.payment_status === 'trial' && new Date() > new Date(data.trial_end_date));

      return { hasPending, subscription: data };

    } catch (err) {
      console.error('Erro ao verificar pagamento pendente:', err);
      return { hasPending: false };
    }
  }, []);

  return {
    isLoading,
    error,
    initiatePayment,
    updateSubscriptionAfterPayment,
    checkPendingPayment
  };
};
